// ABOUTME: Workspace settings panel for viewing/editing workspace details and default location.
// ABOUTME: Also exports DeleteWorkspaceCard for danger-zone workspace deletion with confirmation.

import { useState, useEffect, useRef } from 'react';
import { Building2, Loader2, Check, X, Edit2, MapPin, Search, Trash2, AlertTriangle  } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { trpc } from '@/lib/trpc';
import type { UpdateWorkspaceRequest, WorkspaceDefaultLocation } from '@/types/workspace';
import type { Store } from '@/types';

export function WorkspaceSettings() {
    const { currentWorkspace, isAdmin, refreshCurrentWorkspace, refreshWorkspaces } = useWorkspace();

    const [editing, setEditing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<UpdateWorkspaceRequest>({
        name: currentWorkspace?.name || '',
        companyName: currentWorkspace?.companyName || '',
        businessEmail: currentWorkspace?.businessEmail || '',
        businessAddress: currentWorkspace?.businessAddress || '',
        gstCode: currentWorkspace?.gstCode || '',
    });

    // Default location state
    const [selectedLocation, setSelectedLocation] = useState<WorkspaceDefaultLocation | null>(
        currentWorkspace?.defaultLocation ?? null
    );
    const [locationQuery, setLocationQuery] = useState('');
    const [debouncedLocationQuery, setDebouncedLocationQuery] = useState('');
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const locationDropdownRef = useRef<HTMLDivElement>(null);

    // tRPC hooks
    const updateWorkspaceMutation = trpc.workspace.update.useMutation();
    const loading = updateWorkspaceMutation.isPending;

    const storeAutocompleteQuery = trpc.store.autocomplete.useQuery(
        { query: debouncedLocationQuery, limit: 10 },
        { enabled: debouncedLocationQuery.length >= 2 }
    );
    const locationResults = (storeAutocompleteQuery.data ?? []) as unknown as Store[];
    const locationSearching = storeAutocompleteQuery.isLoading && debouncedLocationQuery.length >= 2;

    // Reset form data when workspace changes
    useEffect(() => {
        setFormData({
            name: currentWorkspace?.name || '',
            companyName: currentWorkspace?.companyName || '',
            businessEmail: currentWorkspace?.businessEmail || '',
            businessAddress: currentWorkspace?.businessAddress || '',
            gstCode: currentWorkspace?.gstCode || '',
        });
        setSelectedLocation(currentWorkspace?.defaultLocation ?? null);
        setEditing(false);
        setError(null);
    }, [currentWorkspace?.id]);

    // Debounce location search query
    useEffect(() => {
        const timer = setTimeout(() => {
            if (locationQuery.trim().length >= 2) {
                setDebouncedLocationQuery(locationQuery.trim());
                setShowLocationDropdown(true);
            } else {
                setDebouncedLocationQuery('');
                setShowLocationDropdown(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [locationQuery]);

    // Close location dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target as Node)) {
                setShowLocationDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectLocation = (store: Store) => {
        setSelectedLocation({
            storeId: store.storeId,
            name: store.name,
            address: store.address,
            latitude: store.latitude,
            longitude: store.longitude,
            cityId: store.cityId,
            cityName: store.cityName,
        });
        setLocationQuery('');
        setShowLocationDropdown(false);
    };

    const handleClearLocation = () => {
        setSelectedLocation(null);
        setLocationQuery('');
    };

    const handleEdit = () => {
        setFormData({
            name: currentWorkspace?.name || '',
            companyName: currentWorkspace?.companyName || '',
            businessEmail: currentWorkspace?.businessEmail || '',
            businessAddress: currentWorkspace?.businessAddress || '',
            gstCode: currentWorkspace?.gstCode || '',
        });
        setSelectedLocation(currentWorkspace?.defaultLocation ?? null);
        setEditing(true);
        setError(null);
    };

    const handleCancel = () => {
        setSelectedLocation(currentWorkspace?.defaultLocation ?? null);
        setLocationQuery('');
        setShowLocationDropdown(false);
        setEditing(false);
        setError(null);
    };

    const handleSave = async () => {
        if (!currentWorkspace || !isAdmin) return;

        if (!formData.name?.trim()) {
            setError('Workspace name is required');
            return;
        }

        setError(null);

        try {
            await updateWorkspaceMutation.mutateAsync({
                workspaceId: currentWorkspace.id,
                ...formData,
                defaultLocation: selectedLocation,
            });
            await Promise.all([refreshCurrentWorkspace(), refreshWorkspaces()]);
            setEditing(false);
        } catch (err) {
            console.error('Failed to update workspace:', err);
            setError(err instanceof Error ? err.message : 'Failed to update workspace');
        }
    };

    if (!currentWorkspace) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                    No workspace selected
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Workspace Details</CardTitle>
                            <CardDescription>
                                {isAdmin
                                    ? 'Manage your workspace settings'
                                    : 'View workspace information'}
                            </CardDescription>
                        </div>
                    </div>
                    {isAdmin && !editing && (
                        <Button variant="outline" size="sm" onClick={handleEdit}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && (
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Workspace Name</Label>
                        {editing ? (
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        ) : (
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.name}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        {editing ? (
                            <Input
                                id="companyName"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        ) : (
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.companyName || '-'}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="businessEmail">Business Email</Label>
                        {editing ? (
                            <Input
                                id="businessEmail"
                                name="businessEmail"
                                type="email"
                                value={formData.businessEmail}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        ) : (
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.businessEmail || '-'}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="gstCode">GST Code</Label>
                        {editing ? (
                            <Input
                                id="gstCode"
                                name="gstCode"
                                value={formData.gstCode}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        ) : (
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.gstCode || '-'}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="businessAddress">Business Address</Label>
                        {editing ? (
                            <Input
                                id="businessAddress"
                                name="businessAddress"
                                value={formData.businessAddress}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        ) : (
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.businessAddress || '-'}
                            </p>
                        )}
                    </div>
                </div>

                {/* Default Location */}
                <div className="pt-4 border-t border-border">
                    <div className="space-y-2">
                        <Label>Default Location</Label>
                        <p className="text-xs text-muted-foreground">
                            The default store used for product searches and price tracking across this workspace.
                        </p>
                        {editing ? (
                            <div className="relative" ref={locationDropdownRef}>
                                {selectedLocation ? (
                                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-background text-primary">
                                            <MapPin className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">
                                                {selectedLocation.name}, {selectedLocation.cityName}
                                            </p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {selectedLocation.address}
                                            </p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleClearLocation}
                                            disabled={loading}
                                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search stores (e.g. Indiranagar, Koramangala)..."
                                            value={locationQuery}
                                            onChange={(e) => setLocationQuery(e.target.value)}
                                            disabled={loading}
                                            className="pl-9"
                                        />
                                        {locationSearching && (
                                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                                        )}
                                    </div>
                                )}

                                {/* Search Results Dropdown */}
                                {showLocationDropdown && locationResults.length > 0 && !selectedLocation && (
                                    <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                                        {locationResults.map((store) => (
                                            <button
                                                key={store.storeId}
                                                type="button"
                                                onClick={() => handleSelectLocation(store)}
                                                className="w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted"
                                            >
                                                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-transparent bg-muted text-muted-foreground">
                                                    <MapPin className="h-4 w-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium leading-snug">
                                                        {store.name}
                                                    </p>
                                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                                                        <span>{store.cityName}</span>
                                                        <span>·</span>
                                                        <span className="font-mono">#{store.storeId}</span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground/70 leading-snug truncate mt-0.5">
                                                        {store.address}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {showLocationDropdown && debouncedLocationQuery.length >= 2 && !locationSearching && locationResults.length === 0 && !selectedLocation && (
                                    <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg p-4 text-center">
                                        <p className="text-sm text-muted-foreground">No stores found</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            currentWorkspace.defaultLocation ? (
                                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-background text-primary">
                                        <MapPin className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">
                                            {currentWorkspace.defaultLocation.name}, {currentWorkspace.defaultLocation.cityName}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {currentWorkspace.defaultLocation.address}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg text-muted-foreground">
                                    Not set
                                </p>
                            )
                        )}
                    </div>
                </div>

                {/* Status and Plan Info */}
                <div className="pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${currentWorkspace.status === 'paid'
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : currentWorkspace.status === 'suspended'
                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    }`}>
                                    {currentWorkspace.status.charAt(0).toUpperCase() + currentWorkspace.status.slice(1)}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Plan</Label>
                            <p className="text-sm py-2 px-3 bg-muted/50 rounded-lg">
                                {currentWorkspace.pricingModel?.displayName || 'Free'}
                            </p>
                        </div>
                    </div>
                </div>

                {editing && (
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={loading}>
                            {loading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="h-4 w-4 mr-2" />
                            )}
                            Save Changes
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Delete Workspace Card Component
export function DeleteWorkspaceCard() {
    const { currentWorkspace, isAdmin, deleteCurrentWorkspace, workspaces } = useWorkspace();
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    if (!currentWorkspace || !isAdmin) {
        return null;
    }

    const canDelete = workspaces.length > 1;
    const confirmationText = currentWorkspace.name;

    const handleDelete = async () => {
        if (confirmText !== confirmationText) {
            setDeleteError('Please type the workspace name exactly to confirm');
            return;
        }

        setDeleting(true);
        setDeleteError(null);

        try {
            await deleteCurrentWorkspace();
            setShowConfirm(false);
        } catch (err) {
            setDeleteError(err instanceof Error ? err.message : 'Failed to delete workspace');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Card className="border-destructive/50">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-destructive/10">
                        <Trash2 className="h-5 w-5 text-destructive" />
                    </div>
                    <div>
                        <CardTitle className="text-lg text-destructive">Danger Zone</CardTitle>
                        <CardDescription>
                            Permanently delete this workspace
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {!showConfirm ? (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Once you delete a workspace, there is no going back. This will permanently delete:
                        </p>
                        <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                            <li>All tracked products and competitor data</li>
                            <li>All workspace settings and configurations</li>
                            <li>All member access to this workspace</li>
                        </ul>
                        {!canDelete && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-sm">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                <span>You cannot delete your only workspace. Create another workspace first.</span>
                            </div>
                        )}
                        <Button
                            variant="destructive"
                            onClick={() => setShowConfirm(true)}
                            disabled={!canDelete}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Workspace
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span>This action cannot be undone!</span>
                        </div>

                        {deleteError && (
                            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                {deleteError}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="confirmDelete">
                                Type <span className="font-mono font-bold">{confirmationText}</span> to confirm
                            </Label>
                            <Input
                                id="confirmDelete"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type workspace name to confirm"
                                disabled={deleting}
                            />
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowConfirm(false);
                                    setConfirmText('');
                                    setDeleteError(null);
                                }}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={deleting || confirmText !== confirmationText}
                            >
                                {deleting ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Permanently Delete
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
